/* ============================================================
   Module introductions — displayed as dismissible panels
   at the top of each module's overview.

   Proposal 6 Tier 1 (2026-04-24) — introductions everywhere so
   new users understand what they're navigating to. Lens has a
   lot of surface area; these 2-3 sentence primers cut through it.

   Keyed by module ID (post-Phase-2 rename 2026-04-25:
   actuals / budgets / forecasts / synthesis / fcc / settings).
   Each entry: { title, body }. Dismissal is per-module,
   per-browser, via local browser state.
   ============================================================ */

export const MODULE_INTROS = {
  actuals: {
    title: 'Actuals · the closed books',
    body: 'Your Lens view of historical actuals, labeled by source-state proof. When the visible Book scope, currentness, provider, and row-authorship gates are available, these views can carry strict Ledger truth; otherwise they stay clearly held, sourced, or computed. Start here when a conversation is about what actually happened, then read the proof chips before making a strict claim.',
  },
  budgets: {
    title: 'Budgets · plan and operational pulse',
    body: 'Your plan meeting reality. Recurring subscriptions, AR/AP cycles, DSO/DPO/CCC working-capital ratios, and committed payment schedules. Mix of Ledger data (what actually cleared) plus your recurring plans (what is scheduled). Ratios are computed from the Ledger; schedules are your own parameters.',
  },
  forecasts: {
    title: 'Forecasts · project the future',
    body: 'Drivers, overlays, and model assumptions. Model runway, revenue, cost, expense, operations, or risk 12+ months out. No Ledger data directly — every forecast starts from your Actuals + Budgets baseline, then layers your assumptions on top. Adjust assumptions, watch the curve shift.',
  },
  'support-stack': {
    title: 'Support Stack · schedules, trackers, sub-ledger explanation',
    body: 'A read-only Lens shell for the support evidence around Actuals, Budgets, and Forecasts. Sub-ledgers explain balances, Schedules explain timing, and Trackers explain status or variance. A row becomes actual only when posted in Ledger, usually through a JE or approved valuation entry.',
  },
  'cash-monitor': {
    title: 'Cash Monitor · daily cash judgment',
    body: 'Daily cash visibility for evidence cash, Expected receipts, Committed obligations, Optional payments, Assumptions, Plan variants, and Source-local/demo rows. Ledger remains the accounting evidence source; Cash Monitor is the Lens judgment layer for timing, gaps, tight-budget planning, and plan comparison.',
  },
  synthesis: {
    title: 'Synthesis · where Actuals, Budgets and Forecasts meet',
    body: 'Variance waterfalls, trajectory charts, cross-module composition. Soon: toggle which data streams contribute to any view (include Actuals / Budgets / Forecasts independently). Treat it as your sandbox for "what do they agree on, where do they diverge?"',
  },
  fcc: {
    title: 'Command Module · simulation sandbox',
    body: 'Your local model sandbox. Model buy/sell decisions, NC-Assets, NC-Liabilities, draft journal entries, and run the 3-Statement model. Simulations propagate to Actuals, Budgets, or Forecasts based on your choices; the sandbox stays distinct from your real ledger until an approved commit path exists.',
  },
  drivers: {
    title: 'Model Drivers · the levers that shape your numbers',
    body: 'Where you author the parameter data that drives every other module. Recurring schedules, universal model drivers, macro overlays, seasonal factors, and model schedules/pins cover business, finance, revenue, cost, expense, and operations models. Edit here, see the impact ripple through Budgets and Forecasts. Designed all-encompassing for any size, country standard, or currency. Multi-tenant: this layer becomes Book-scoped under F-023.',
  },
  settings: {
    title: 'Settings · app behavior',
    body: 'App-level controls for model assumptions, profiles, account visibility, presentation density, guides, and snapshots. These change how Lens renders; they never change your data. Safe to experiment — every setting is reversible.',
  },
};
